import { ChangeEvent, useMemo, useState } from 'react';

import { fileReaderUtils } from '@pukka/common/utils';
import { toast } from '@pukka/toast';
import { Button, Icon, ImageUpload, ImageUploadFileType, ImageUploadProps, InformationBox, Text } from '@pukka/ui-components';
import { useSnapshot } from 'valtio';

import { FormInput } from './FormInput';
import { BusinessFormState, getFileValue, onRemoveFile, onUpload, ValidationState } from './GopayActivationForm.state';

interface BusinessUploadFormProps {
  readonly BusinessState?: typeof BusinessFormState;
  readonly Validation?: typeof ValidationState;
}

interface SkUploadInputProps {
  readonly name: 'skUpload' | 'modifiedSkUpload';
  readonly value: readonly [string, string] | undefined;
}

const MAX_FILE_MB_SIZE = 2;

const RULE: ImageUploadProps<unknown>['rule'] = {
  maxCount: 1,
  maxSize: [MAX_FILE_MB_SIZE * 1_000_000, `Max size is ${MAX_FILE_MB_SIZE} MB`],
  errorMessage: 'This is a required question',
};

function SkUploadInput({ name, value }: SkUploadInputProps) {
  const [isSizeError, setIsSizeError] = useState(false);

  const handleUploadPdf = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const { files } = e.target;

      if (files[0]?.size > MAX_FILE_MB_SIZE * 1_000_000) {
        setIsSizeError(true);

        return;
      }

      setIsSizeError(false);

      try {
        const base64 = await fileReaderUtils.getBase64(files[0]);

        const fileData = { name: files[0].name, url: base64 as string } as ImageUploadFileType<unknown>;

        onUpload('business', name)([fileData]);
      } catch (error) {
        console.error(error);

        toast.error('Error uploading file. Please try again or check your file');
      }
    }
  };

  return value ? (
    <div className="flex">
      <Text>{value[0]}</Text>
      <Button variant="nude" size="none" className="ml-1.5 rounded-md p-0.5" onClick={onRemoveFile('business', name)}>
        <Icon icon="wrong" size="sm" color="red" />
      </Button>
    </div>
  ) : (
    <div>
      {isSizeError && (
        <InformationBox variant="danger" className="mb-2">
          Max size is {MAX_FILE_MB_SIZE} MB
        </InformationBox>
      )}
      <input type="file" name={name} data-testid={name} accept="application/pdf" onChange={handleUploadPdf} />
    </div>
  );
}

function BusinessUploadForm({ BusinessState = BusinessFormState, Validation = ValidationState }: BusinessUploadFormProps) {
  const { dirty, idUpload, modifiedSkUpload, npwpUpload, siupUpload, skUpload } = useSnapshot(BusinessState);
  const { businessValidation } = useSnapshot(Validation);

  const idUploadValue = useMemo(() => {
    return getFileValue(idUpload);
  }, [idUpload]);

  const npwpUploadValue = useMemo(() => {
    return getFileValue(npwpUpload);
  }, [npwpUpload]);

  const siupUploadValue = useMemo(() => {
    return getFileValue(siupUpload);
  }, [siupUpload]);

  return (
    <div>
      <Text as="h3" variant="title-2" weight="bold" className="mb-4" color="blue">
        Dokumentasi Badan Usaha
      </Text>
      <FormInput
        required
        label="Foto KTP/KITAS"
        description="KITAS diperlukan jika direktur adalah WNA (Warga Negara Asing)."
        error={dirty.has('idUpload') && !businessValidation.idUpload.success && businessValidation.idUpload.error}
      >
        <ImageUpload testId="idUpload" ordering={false} rule={RULE} images={idUploadValue} onUpdate={onUpload('business', 'idUpload')} />
      </FormInput>
      <FormInput
        required
        label="Foto NPWP Badan Usaha"
        description="WAJIB dilengkapi untuk badan usaha."
        error={dirty.has('npwpUpload') && !businessValidation.npwpUpload.success && businessValidation.npwpUpload.error}
      >
        <ImageUpload
          testId="npwpUpload"
          ordering={false}
          rule={RULE}
          images={npwpUploadValue}
          onUpdate={onUpload('business', 'npwpUpload')}
        />
      </FormInput>
      <FormInput
        required
        label="Foto Akta & SK Pendirian"
        description="WAJIB dilengkapi untuk badan usaha. (dalam bentuk PDF)"
        error={dirty.has('skUpload') && !businessValidation.skUpload.success && businessValidation.skUpload.error}
      >
        <SkUploadInput name="skUpload" value={skUpload} />
      </FormInput>
      <FormInput
        required
        label="Foto Akta & SK Perubahan"
        description="Jika akta sebelumnya sudah expired lebih dari 5 tahun. (dalam bentuk PDF)"
        error={dirty.has('modifiedSkUpload') && !businessValidation.modifiedSkUpload.success && businessValidation.modifiedSkUpload.error}
      >
        <SkUploadInput name="modifiedSkUpload" value={modifiedSkUpload} />
      </FormInput>
      <FormInput
        required
        label="Foto SIUP/NIB"
        description="WAJIB dilengkapi untuk badan usaha."
        error={dirty.has('siupUpload') && !businessValidation.siupUpload.success && businessValidation.siupUpload.error}
      >
        <ImageUpload
          testId="siupUpload"
          ordering={false}
          rule={RULE}
          images={siupUploadValue}
          onUpdate={onUpload('business', 'siupUpload')}
        />
      </FormInput>
    </div>
  );
}

export { BusinessUploadForm, SkUploadInput };
